import { FormikErrors } from 'formik'
import i18n from 'src/i18n'
import {
  regex_50cc,
  regex_cuclass,
  regex_date,
  regex_itclass,
  regex_nclaims,
  regex_plate,
  regex_taxcode,
  regex_year,
} from 'src/regex'
import * as Yup from 'yup'

import { dict, option_t } from '../commons.types'

/*********
 *  Yup  *
 *********/
export type yup_t =
  | 'YBOOL'
  | 'YNUM'
  | 'YSTR'
  | 'YEMAIL'
  | 'YDATE'
  | 'YYEAR'
  | 'YPLATE'
  | 'YTAXCODE'
  | 'YCUCLASS'
  | 'YITCLASS'
  | 'YNCLAIMS'
  | 'Y50CC'

export const YUPS = (message?: string): Record<yup_t, Yup.BaseSchema> => ({
  YBOOL: Yup.bool(),
  YNUM: Yup.number().typeError(i18n.t('commons.invalid_num')).min(0, i18n.t('commons.invalid_num')),
  YSTR: Yup.string(),
  YEMAIL: Yup.string().email(message || i18n.t('commons.invalid_email')),
  YDATE: Yup.string().matches(regex_date, message || i18n.t('commons.invalid_date')),
  YYEAR: Yup.string().matches(regex_year, message || i18n.t('commons.invalid_year')),
  YPLATE: Yup.string().matches(regex_plate, message || i18n.t('commons.invalid_plate')),
  YTAXCODE: Yup.string().matches(regex_taxcode, message || i18n.t('commons.invalid_taxcode')),
  YCUCLASS: Yup.string().matches(regex_cuclass, message || i18n.t('commons.invalid_num')),
  YITCLASS: Yup.string().matches(regex_itclass, message || i18n.t('commons.invalid_num')),
  YNCLAIMS: Yup.string().matches(regex_nclaims, message || i18n.t('commons.invalid_num')),
  Y50CC: Yup.string().matches(regex_50cc, message || i18n.t('commons.invalid_for_entity')),
})

/************
 *  Formik  *
 ************/
export type formik_t = 'INPUT_TEXT' | 'SELECT_MENU' | 'RADIO_GROUP' | 'CHECK_BOX' | 'DATE_PICKER'

export const FORMIK_WIDTHS = {
  20: 'col-lg-2',
  50: 'col-lg-6',
  100: 'col-lg-12',
}

export interface fdepend_t {
  disabled?: dict<boolean>
  hidden?: dict<boolean>
  precompiled?: dict<string>
  domains?: dict<dict<option_t[]>>
}

export interface fstate_t {
  fpool: fitem_t[]
  fired: boolean
  valid: boolean
  gtm_class?: string
}

export interface formset_t {
  step?: string
  fired?: boolean
  disabled?: boolean
  classes?: string
  on_change?: (state: fstate_t) => void
  on_submit?: (state: fstate_t) => void
  children?: (state: fstate_t) => JSX.Element
}

export interface fitem_t {
  // Generic
  id: string
  entity?: string
  order?: number
  sector?: string
  sector_descr?: string
  type?: formik_t
  label?: string
  required?: boolean
  disabled?: boolean
  hidden?: boolean
  width?: 20 | 50 | 100
  capture?: string
  tooltip?: string
  yup_type?: yup_t
  yup_message?: string

  // Specific
  title?: string
  description?: string
  placeholder?: string
  input_textarea?: boolean
  input_type?: 'DEFAULT' | 'SELECT'
  radio_type?: 'DEFAULT' | 'SQUARED'
  select_type?: 'DEFAULT' | 'SEARCH'
  options?: option_t[]
  icon?: string
  icon_size?: number
  privacy?: boolean
  disabled_from?: string
  disabled_to?: string

  // Dependencies
  dependencies?: dict<fdepend_t>

  // Derived
  error?: string
  fired?: boolean
  uppercase?: boolean
  warning?: string

  // Classes
  classes?: string
  classes_field?: string
  classes_label?: string
  classes_input?: string
  classes_error?: string
  classes_icon?: string
  classes_option?: string
  classes_disabled?: string
  classes_tooltip?: string
  classes_description?: string
  out_focus?: boolean

  // Handlers
  on_focus_capture?: (active: boolean) => void
  on_press?: () => void
  on_press_icon?: () => void
  on_change?: (value: string) => void
  set_field_value?: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<dict<string>>>
}
